import { useEffect } from 'react';
import { Outlet, Link } from 'react-router-dom';
import { useBranding } from '../../hooks/useBranding';
import SandboxBanner from '../common/SandboxBanner';

/**
 * Authentication pages layout
 * Always uses light mode regardless of user's dark mode preference
 *
 * Branded at runtime: the name, the login logo and the background image
 * come from the resolve endpoint (brandingStore), which answered before
 * React rendered — so a visitor on a tenant's hostname never sees another
 * MSP's name, even for a frame.
 */
export default function AuthLayout() {
  const branding = useBranding();

  // Force light mode on auth pages
  useEffect(() => {
    const wasDark = document.documentElement.classList.contains('dark');
    document.documentElement.classList.remove('dark');

    return () => {
      // Restore dark mode if it was enabled
      if (wasDark) {
        document.documentElement.classList.add('dark');
      }
    };
  }, []);

  const year = new Date().getFullYear();
  const backgroundStyle = branding.loginBackgroundUrl
    ? { backgroundImage: `url("${branding.loginBackgroundUrl}")`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : undefined;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Before anyone signs in: a sandbox says so on the login page itself. */}
      <SandboxBanner />
      <div className="flex flex-1">
      {/* Left side - Branding */}
      <div
        className="relative hidden lg:flex lg:w-1/2 bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 p-12 flex-col justify-between"
        style={backgroundStyle}
      >
        {/* A photo behind white text needs darkening; the gradient alone does not. */}
        {branding.loginBackgroundUrl && <div className="absolute inset-0 bg-neutral-950/60" aria-hidden="true" />}
        <div className="relative">
          <Link to="/" className="inline-flex items-center gap-2">
            <BrandMark branding={branding} className="text-3xl font-bold text-white" imgClassName="h-12 max-w-[240px]" />
          </Link>
        </div>

        <div className="relative max-w-md">
          <h1 className="text-4xl font-bold text-white mb-6">
            Welcome to Your<br />Client Portal
          </h1>
          <p className="text-neutral-400 text-lg leading-relaxed">
            Manage your tickets, view invoices, track projects, and more — all in one place.
          </p>

          <div className="mt-12 space-y-6">
            <Feature
              icon="📋"
              title="Support Tickets"
              description="Create and track support requests in real-time"
            />
            <Feature
              icon="💳"
              title="Billing & Payments"
              description="View invoices and make payments online"
            />
            <Feature
              icon="📄"
              title="Quotes & Proposals"
              description="Review and accept quotes electronically"
            />
          </div>
        </div>

        <div className="relative text-neutral-500 text-sm">
          © {year} {branding.companyName}
          {branding.poweredBy && <span className="ml-2 text-neutral-600">· Powered by HaloMTS</span>}
        </div>
      </div>

      {/* Right side - Auth forms */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2">
              <BrandMark branding={branding} className="text-2xl font-bold text-neutral-900" imgClassName="h-10 max-w-[200px] mx-auto" />
            </Link>
          </div>

          <Outlet />
        </div>
      </div>
      </div>
    </div>
  );
}

/** The tenant's login logo when it has one, otherwise its name. */
function BrandMark({ branding, className, imgClassName }) {
  if (branding.loginLogoUrl) {
    return <img src={branding.loginLogoUrl} alt={branding.portalName} className={`${imgClassName} object-contain`} />;
  }
  return <span className={className}>{branding.portalName}</span>;
}

function Feature({ icon, title, description }) {
  return (
    <div className="flex items-start gap-4">
      <span className="text-2xl">{icon}</span>
      <div>
        <h3 className="text-white font-medium">{title}</h3>
        <p className="text-neutral-400 text-sm">{description}</p>
      </div>
    </div>
  );
}
